import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IncomingInvoice, InboxHealth, LedgerParty, ReceiptMatchCandidate } from "../types";
import { incomingInvoiceService } from "../services/dataService";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { documentStateName, flattenHistory, HistoryEvent } from "../utils/documentHistory";
import PageLoader from "../components/PageLoader";
import { usePermissions } from "../context/PermissionContext";
import "./Documents.css";
import "./IncomingInvoices.css";

/**
 * Gelen Faturalar — tedarikçilerden (ve iade eden müşterilerden) gelen e-Faturalar.
 *
 * Kaynak e-belge platformunun gelen kutusudur; platform EDM'den periyodik çeker,
 * "Gelen kutusunu çek" hemen çektirir. Liste, çıktılar ve kabul/red backend
 * üzerinden proxy'lenir. Bu ekranda yapılan iki iş:
 *  - Ticari faturaya süresi içinde kabul/red yanıtı (8 gün; süre dolunca kabul sayılır).
 *  - Faturayı cariye işlemek: alış faturası tedarikçi borcuna, iade faturası müşteri alacağına.
 *
 * e-Arşiv faturalar GİB gelen kutusuna düşmez (alıcıya e-postayla gider); burada görünmez.
 */

/* ---------- yardımcılar ---------- */

const errorText = (error: any, fallback: string): string => {
  const d = error?.response?.data;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (d.detail && d.title) return `${d.title}: ${d.detail}`;
  return d.detail || d.title || fallback;
};

type Tone = "ok" | "info" | "warn" | "err" | "muted";

const SCENARIOS: Record<string, string> = {
  TemelFatura: "Temel",
  TicariFatura: "Ticari",
  EArsivFatura: "e-Arşiv",
  Ihracat: "İhracat",
  YolcuBeraberFatura: "Yolcu beraber",
};
const TYPES: Record<string, string> = {
  Satis: "Satış",
  Iade: "İade",
  Tevkifat: "Tevkifat",
  Istisna: "İstisna",
  OzelMatrah: "Özel matrah",
  IhracKayitli: "İhraç kayıtlı",
  Sgk: "SGK",
  KonaklamaVergisi: "Konaklama vergisi",
};
const scenarioName = (s: string) => SCENARIOS[s] || s;
const typeName = (t?: string | null) => (t ? TYPES[t] || t : "—");

const statusOf = (d: IncomingInvoice): { text: string; tone: Tone } => {
  if (d.applicationResponse === "KABUL") return { text: "Kabul edildi", tone: "ok" };
  if (d.applicationResponse === "RED") return { text: "Reddedildi", tone: "err" };
  if (d.canRespond && d.daysLeft != null) {
    return d.daysLeft === 0
      ? { text: "Yanıt bugün", tone: "warn" }
      : { text: `Yanıt · ${d.daysLeft} gün`, tone: d.daysLeft <= 2 ? "warn" : "info" };
  }
  if (d.scenario === "TicariFatura" && d.daysLeft != null && d.daysLeft < 0) return { text: "Süre doldu", tone: "muted" };
  if (d.state === "Failed") return { text: "Hata", tone: "err" };
  if (d.state === "Cancelled") return { text: "İptal", tone: "warn" };
  if (d.state === "Delivered" || d.state === "GibAccepted") return { text: "Teslim alındı", tone: "ok" };
  return { text: documentStateName(d.state), tone: "info" };
};

/** Yerel takvim günü (toISOString UTC'ye çevirip TR'de bir gün geri kaydırır). */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** "yyyy-MM-dd" (DateOnly) → "dd.MM.yyyy"; Date'e çevirmek saat dilimine göre günü kaydırabilirdi. */
const fmtDay = (s?: string | null) => {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : s;
};
const fmtDateTime = (s?: string | null) => (s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

const NEW_SUPPLIER = "__new";

/* ---------- ikonlar ---------- */

const I = {
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
  ),
  inboxIn: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v10" /><path d="m8 9 4 4 4-4" /><path d="M4 13h4l2 3h4l2-3h4v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /></svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>
  ),
  file: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M4 20h16" /></svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6" /></svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><path d="M12 17h.01" /></svg>
  ),
  ledger: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" /></svg>
  ),
  inbox: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>
  ),
  filterOff: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>
  ),
};

/* ---------- küçük bileşenler ---------- */

const Badge: React.FC<{ tone: Tone | "efatura" | "proforma"; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => (
  <span className={`ed-badge ${tone}`} title={title}>
    {children}
  </span>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="ed-field">
    <span>
      {label}
      {hint && <em>{hint}</em>}
    </span>
    {children}
  </label>
);

/* ---------- sayfa ---------- */

type View = "" | "respond" | "unposted" | "return" | "unread";
type DatePreset = "7" | "30" | "month" | "year" | "all";

interface PostForm {
  supplierId: string; // "" seçilmedi, NEW_SUPPLIER = faturadaki bilgilerle aç
  firmId: string;
  matchEntryId: string; // "" = yeni kayıt
  dueDate: string;
}

const IncomingInvoices: React.FC = () => {
  const { has, hasModule } = usePermissions();
  const canApprove = has("incoming-invoices:approve");
  const canPost = has("incoming-invoices:post");
  const financeOn = hasModule("finance");
  const receivablesOn = hasModule("receivables");

  const [items, setItems] = useState<IncomingInvoice[]>([]);
  const [health, setHealth] = useState<InboxHealth | null>(null);
  const [initial, setInitial] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  // Süzgeçler
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Detay çekmecesi
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<IncomingInvoice | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Kabul / red
  const [respond, setRespond] = useState<{ mode: "accept" | "reject" | null; reason: string }>({ mode: null, reason: "" });

  // Cariye işleme
  const [parties, setParties] = useState<{ suppliers: LedgerParty[]; firms: LedgerParty[] } | null>(null);
  const [post, setPost] = useState<PostForm>({ supplierId: "", firmId: "", matchEntryId: "", dueDate: "" });
  const [candidates, setCandidates] = useState<ReceiptMatchCandidate[]>([]);

  /* ---- yükleme ---- */

  const load = async () => {
    setLoading(true);
    try {
      const [list, h] = await Promise.all([
        incomingInvoiceService.list({ from: dateFrom || undefined, to: dateTo || undefined }),
        incomingInvoiceService.health().catch(() => null),
      ]);
      setItems(list);
      setHealth(h);
    } catch (error: any) {
      setNotice({ ok: false, text: errorText(error, "Gelen faturalar yüklenemedi.") });
    } finally {
      setLoading(false);
      setInitial(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const handleSync = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      const r = await incomingInvoiceService.sync();
      setNotice({
        ok: !r.incompleteSince,
        text: r.incompleteSince
          ? `Gelen kutusu çekildi ama ${fmtDateTime(r.incompleteSince)} sonrasında eksik belge olabilir. Softana ile iletişime geçin.`
          : r.added > 0
            ? `Gelen kutusu çekildi: ${r.added} yeni belge.`
            : "Gelen kutusu çekildi: yeni belge yok.",
      });
      await load();
    } catch (error: any) {
      setNotice({ ok: false, text: errorText(error, "Gelen kutusu çekilemedi.") });
    } finally {
      setSyncing(false);
    }
  };

  /* ---- süzme / özet ---- */

  const filtered = useMemo(
    () =>
      items.filter((d) => {
        if (view === "respond" && !d.canRespond) return false;
        if (view === "unposted" && d.postedTo) return false;
        if (view === "return" && !d.isReturn) return false;
        if (view === "unread" && d.markedRead) return false;
        if (search) {
          const hit =
            turkishIncludes(d.invoiceNumber || "", search) ||
            turkishIncludes(d.senderName || "", search) ||
            turkishIncludes(d.senderIdentifier || "", search) ||
            turkishIncludes(d.supplierName || "", search) ||
            turkishIncludes(d.firmName || "", search);
          if (!hit) return false;
        }
        return true;
      }),
    [items, view, search],
  );

  const summary = useMemo(() => {
    const unread = filtered.filter((d) => !d.markedRead).length;
    const respondable = filtered.filter((d) => d.canRespond);
    const urgent = respondable.filter((d) => (d.daysLeft ?? 99) <= 2).length;
    const nearest = respondable.reduce<number | null>(
      (m, d) => (d.daysLeft == null ? m : m == null ? d.daysLeft : Math.min(m, d.daysLeft)),
      null,
    );
    const unposted = filtered.filter((d) => !d.postedTo).length;
    const totals: Record<string, number> = {};
    for (const d of filtered) {
      if (d.isReturn) continue;
      totals[d.currencyCode] = (totals[d.currencyCode] || 0) + d.payableAmount;
    }
    const currencies = Object.keys(totals).sort((a, b) => (a === "TRY" ? -1 : b === "TRY" ? 1 : a.localeCompare(b)));
    return { total: filtered.length, unread, respondable: respondable.length, urgent, nearest, unposted, totals, currencies };
  }, [filtered]);

  const hasFilter = !!(search || view || dateFrom || dateTo);
  const clearFilters = () => {
    setSearch("");
    setView("");
    setDateFrom("");
    setDateTo("");
  };

  const activePreset = (): DatePreset | null => {
    const today = new Date();
    if (!dateFrom && !dateTo) return "all";
    if (dateTo !== iso(today)) return null;
    const from = new Date(dateFrom);
    const diff = Math.round((today.getTime() - from.getTime()) / 86400000);
    if (diff === 6) return "7";
    if (diff === 29) return "30";
    if (dateFrom === iso(new Date(today.getFullYear(), today.getMonth(), 1))) return "month";
    if (dateFrom === iso(new Date(today.getFullYear(), 0, 1))) return "year";
    return null;
  };

  const applyPreset = (p: DatePreset) => {
    const today = new Date();
    if (p === "all") {
      setDateFrom("");
      setDateTo("");
      return;
    }
    let from: Date;
    if (p === "7") from = new Date(today.getTime() - 6 * 86400000);
    else if (p === "30") from = new Date(today.getTime() - 29 * 86400000);
    else if (p === "month") from = new Date(today.getFullYear(), today.getMonth(), 1);
    else from = new Date(today.getFullYear(), 0, 1);
    setDateFrom(iso(from));
    setDateTo(iso(today));
  };

  /* ---- detay ---- */

  const patchRow = (d: IncomingInvoice) => setItems((prev) => prev.map((x) => (x.uuid === d.uuid ? d : x)));

  /** Cariye işleme formunu faturaya göre hazırlar: eşleşen tedarikçi/firma önden seçilir. */
  const preparePost = (d: IncomingInvoice) => {
    setPost({
      supplierId: d.supplierId || (d.isReturn ? "" : NEW_SUPPLIER),
      firmId: d.firmId || "",
      matchEntryId: "",
      dueDate: "",
    });
    setCandidates([]);
    if (canPost && !d.postedTo && !parties) {
      incomingInvoiceService.parties().then(setParties).catch(() => setParties({ suppliers: [], firms: [] }));
    }
  };

  const openDetail = async (uuid: string) => {
    setDetailId(uuid);
    setDetailLoading(true);
    setDetail(null);
    setHistory(null);
    setActionMsg(null);
    setRespond({ mode: null, reason: "" });
    try {
      const d = await incomingInvoiceService.detail(uuid);
      setDetail(d);
      patchRow(d);
      preparePost(d);

      // Açılan belge EDM'de okundu işaretlenir — en iyi çaba, ekranı bekletmez.
      if (!d.markedRead) {
        incomingInvoiceService
          .markRead(uuid)
          .then(() => {
            const read = { ...d, markedRead: true };
            patchRow(read);
            setDetail((cur) => (cur && cur.uuid === uuid ? { ...cur, markedRead: true } : cur));
          })
          .catch(() => { /* yetki / servis sorunu: okundu bilgisi kritik değil */ });
      }
    } catch (error: any) {
      setNotice({ ok: false, text: errorText(error, "Belge detayı yüklenemedi.") });
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  useEffect(() => {
    if (!detailId) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeDetail();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId]);

  // Seçilen tedarikçinin faturası beklenen mal kabul kayıtları (eşleştirme adayları).
  // Yalnız işlenmemiş alış faturasında ve var olan bir tedarikçi seçiliyken anlamlı.
  const candidateSupplierId =
    detail && !detail.isReturn && !detail.postedTo && canPost && financeOn && post.supplierId && post.supplierId !== NEW_SUPPLIER
      ? post.supplierId
      : null;

  useEffect(() => {
    if (!candidateSupplierId) {
      setCandidates([]);
      return;
    }
    let alive = true;
    incomingInvoiceService
      .receiptCandidates(candidateSupplierId)
      .then((c) => alive && setCandidates(c))
      .catch(() => alive && setCandidates([]));
    return () => {
      alive = false;
    };
  }, [candidateSupplierId]);

  const handleRender = async (d: IncomingInvoice, format: "html" | "pdf" | "xml") => {
    setBusy(true);
    try {
      await incomingInvoiceService.openRender(d.uuid, format, d.invoiceNumber);
    } catch (error: any) {
      // Blob isteklerinde hata gövdesi de blob gelir; metne çevir.
      let message = "Çıktı alınamadı.";
      try {
        const blob = error?.response?.data;
        if (blob instanceof Blob) {
          const parsed = JSON.parse(await blob.text());
          message = parsed.detail || parsed.title || message;
        }
      } catch {
        /* varsayılan mesaj kalır */
      }
      if (detailId) setActionMsg({ ok: false, text: message });
      else setNotice({ ok: false, text: message });
    } finally {
      setBusy(false);
    }
  };

  const reloadDetail = async (uuid: string) => {
    const d = await incomingInvoiceService.detail(uuid);
    setDetail(d);
    patchRow(d);
    return d;
  };

  const handleRespond = async () => {
    if (!detail || !respond.mode) return;
    const accept = respond.mode === "accept";
    setBusy(true);
    setActionMsg(null);
    try {
      await incomingInvoiceService.respond(detail.uuid, accept, respond.reason.trim() || undefined);
      setActionMsg({ ok: true, text: accept ? "Kabul yanıtı gönderildi." : "Red yanıtı gönderildi." });
      setRespond({ mode: null, reason: "" });
      await reloadDetail(detail.uuid);
    } catch (error: any) {
      setActionMsg({ ok: false, text: errorText(error, "Yanıt gönderilemedi.") });
    } finally {
      setBusy(false);
    }
  };

  const handlePost = async () => {
    if (!detail) return;
    setBusy(true);
    setActionMsg(null);
    try {
      const r = await incomingInvoiceService.post(
        detail.uuid,
        detail.isReturn
          ? { firmId: post.firmId || null }
          : {
              supplierId: post.supplierId && post.supplierId !== NEW_SUPPLIER ? post.supplierId : null,
              createSupplier: post.supplierId === NEW_SUPPLIER,
              matchEntryId: post.matchEntryId || null,
              dueDate: post.dueDate || null,
            },
      );
      setActionMsg({
        ok: true,
        text: r.postedTo === "firm"
          ? `${r.partyName} carisine alacak olarak işlendi.`
          : `${r.partyName} tedarikçi carisine işlendi.`,
      });
      // Yeni tedarikçi açıldıysa aynı VKN'li diğer faturalar da artık eşleşir: listeyi tazele.
      if (post.supplierId === NEW_SUPPLIER) {
        setParties(null);
        load();
      }
      await reloadDetail(detail.uuid);
    } catch (error: any) {
      setActionMsg({ ok: false, text: errorText(error, "Cariye işlenemedi.") });
    } finally {
      setBusy(false);
    }
  };

  const fetchHistory = async (uuid: string) => {
    setHistoryLoading(true);
    try {
      setHistory(flattenHistory(await incomingInvoiceService.history(uuid)));
    } catch (error: any) {
      setActionMsg({ ok: false, text: errorText(error, "Geçmiş alınamadı.") });
    } finally {
      setHistoryLoading(false);
    }
  };

  if (initial) return <PageLoader message="Gelen faturalar yükleniyor..." />;

  const preset = activePreset();
  const selectedSupplier = parties?.suppliers.find((s) => s.id === post.supplierId);
  const postBlocked = detail
    ? detail.isReturn
      ? !receivablesOn
      : !financeOn
    : true;

  /* ---------- görünüm ---------- */

  return (
    <div className="ed-page">
      {/* Başlık */}
      <header className="ed-head">
        <div>
          <span className="ed-kicker">Faturalama</span>
          <h1>Gelen Faturalar</h1>
          <p>
            Tedarikçilerinizden gelen e-Faturalar. Ticari faturalara süresi içinde kabul/red yanıtı verin,
            faturaları tedarikçi carisine işleyin. e-Arşiv faturalar e-postayla gelir, burada görünmez.
          </p>
          {health && (
            <div className="ii-health">
              {health.synced
                ? <>Gelen kutusu en son <b>{fmtDateTime(health.fetchedThrough)}</b> itibarıyla çekildi.</>
                : <>Gelen kutusu henüz hiç çekilmedi.</>}
            </div>
          )}
        </div>
        <div className="ed-head-actions">
          <button className="ed-btn" onClick={load} disabled={loading || syncing}>
            <span className={loading ? "ed-spin-icon" : ""}>{I.refresh}</span>
            {loading ? "Yenileniyor" : "Listeyi yenile"}
          </button>
          <button className="ed-btn primary" onClick={handleSync} disabled={syncing || loading} title="Entegratördeki yeni belgeleri hemen çek">
            <span className={syncing ? "ed-spin-icon" : ""}>{I.inboxIn}</span>
            {syncing ? "Çekiliyor" : "Gelen kutusunu çek"}
          </button>
        </div>
      </header>

      {health?.incompleteSince && (
        <div className="ed-notice warn">
          {I.alert}
          <span>
            {fmtDateTime(health.incompleteSince)} sonrasında gelen kutusunda eksik belge olabilir (entegratör sayfa sınırı).
            Softana ile iletişime geçin; aralık yeniden çekilebilir.
          </span>
        </div>
      )}

      {/* Özet */}
      <section className="ed-kpis">
        <div className="ed-kpi">
          <span className="l">Gelen fatura</span>
          <span className="v">{summary.total}</span>
          <span className="s">{summary.unread ? `${summary.unread} okunmamış` : hasFilter ? "süzgeçli sonuç" : "tümü okundu"}</span>
        </div>
        <div className={`ed-kpi ${summary.urgent ? "err" : summary.respondable ? "warn" : "ok"}`}>
          <span className="l">Yanıt bekleyen</span>
          <span className="v">{summary.respondable}</span>
          <span className="s">
            {summary.nearest == null
              ? "ticari fatura yok"
              : summary.nearest === 0
                ? "en yakını bugün doluyor"
                : `en yakın süre ${summary.nearest} gün`}
          </span>
        </div>
        <div className={`ed-kpi ${summary.unposted ? "" : "ok"}`}>
          <span className="l">Cariye işlenmemiş</span>
          <span className="v">{summary.unposted}</span>
          <span className="s">{summary.total - summary.unposted} işlendi</span>
        </div>
        <div className="ed-kpi navy">
          <span className="l">Toplam tutar</span>
          <span className="v">
            {summary.currencies.length ? formatCurrency(summary.totals[summary.currencies[0]], summary.currencies[0]) : "—"}
          </span>
          <span className="s">
            {summary.currencies.length > 1
              ? summary.currencies.slice(1).map((c) => formatCurrency(summary.totals[c], c)).join(" · ")
              : "iade faturaları hariç"}
          </span>
        </div>
      </section>

      {/* Araç çubuğu */}
      <section className="ed-toolbar">
        <div className="ed-toolbar-row">
          <div className="ed-search">
            {I.search}
            <input placeholder="Belge no, gönderen, VKN ara" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <button className="ed-ib" aria-label="Temizle" onClick={() => setSearch("")}>
                {I.close}
              </button>
            )}
          </div>
          <div className="ed-dates">
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} title="Belge tarihi (başlangıç)" />
            <span>–</span>
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} title="Belge tarihi (bitiş)" />
          </div>
        </div>

        <div className="ed-toolbar-row chips">
          <div className="ed-chipgroup" role="group" aria-label="Görünüm">
            {(
              [
                ["", "Tümü"],
                ["respond", "Yanıt bekleyen"],
                ["unposted", "Cariye işlenmemiş"],
                ["return", "İade"],
                ["unread", "Okunmamış"],
              ] as const
            ).map(([v, l]) => (
              <button key={v} className={view === v ? "on" : ""} onClick={() => setView(v)}>
                {l}
              </button>
            ))}
          </div>
          <div className="ed-chipgroup" role="group" aria-label="Dönem">
            {(
              [
                ["7", "Son 7 gün"],
                ["30", "Son 30 gün"],
                ["month", "Bu ay"],
                ["year", "Bu yıl"],
                ["all", "Tümü"],
              ] as const
            ).map(([v, l]) => (
              <button key={v} className={preset === v ? "on" : ""} onClick={() => applyPreset(v)}>
                {l}
              </button>
            ))}
          </div>
          {hasFilter && (
            <button className="ed-clear" onClick={clearFilters}>
              {I.filterOff} Süzgeçleri temizle
            </button>
          )}
        </div>
      </section>

      {notice && (
        <div className={`ed-notice ${notice.ok ? "ok" : "err"}`}>
          {notice.ok ? I.check : I.alert}
          <span>{notice.text}</span>
        </div>
      )}

      {/* Liste */}
      <section className={`ed-card ${loading ? "dim" : ""}`}>
        <div className="ed-card-head">
          <span>
            <b>{filtered.length}</b> belge
            {filtered.length !== items.length && <em> / {items.length}</em>}
          </span>
          <span className="ed-hint">Satıra tıklayınca detay açılır · tarih süzgeci belge tarihine göre</span>
        </div>

        {filtered.length === 0 ? (
          <div className="ed-blank">
            <div className="ed-blank-icon">{I.inbox}</div>
            <b>{items.length === 0 ? "Bu dönemde gelen fatura yok" : "Süzgeçle eşleşen belge yok"}</b>
            {items.length === 0
              ? "Entegratörünüze gelen e-Faturalar burada listelenir. Yeni gelenler için \"Gelen kutusunu çek\"i deneyin."
              : "Arama ya da süzgeçleri gevşetmeyi deneyin."}
            {hasFilter && (
              <button className="ed-btn" onClick={clearFilters}>
                Süzgeçleri temizle
              </button>
            )}
          </div>
        ) : (
          <div className="ed-table-wrap">
            <table className="ed-table">
              <thead>
                <tr>
                  <th>Belge</th>
                  <th>Gönderen</th>
                  <th>Durum</th>
                  <th className="num">Tutar</th>
                  <th className="hide-sm">Belge tarihi</th>
                  <th className="hide-md">Cari</th>
                  <th className="act" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const st = statusOf(d);
                  return (
                    <tr
                      key={d.uuid}
                      className={`${d.markedRead ? "" : "ii-unread"} ${detailId === d.uuid ? "on" : ""}`}
                      onClick={() => openDetail(d.uuid)}
                    >
                      <td>
                        <div className="ed-docno">
                          <b>{d.invoiceNumber}</b>
                          {!d.markedRead && <Badge tone="info" title="Henüz açılmadı">Yeni</Badge>}
                        </div>
                        <div className="ed-sub">
                          {scenarioName(d.scenario)}
                          {d.invoiceType && d.invoiceType !== "Satis" ? ` · ${typeName(d.invoiceType)}` : ""}
                        </div>
                      </td>
                      <td>
                        <div className="ed-firm">{d.senderName || "—"}</div>
                        <div className="ed-sub">
                          {d.senderIdentifier ? `VKN/TCKN ${d.senderIdentifier}` : ""}
                          {d.isReturn ? (d.firmName ? ` · müşteri: ${d.firmName}` : "") : d.supplierName ? ` · ${d.supplierName}` : ""}
                        </div>
                      </td>
                      <td>
                        <Badge tone={st.tone}>{st.text}</Badge>
                        {d.isReturn && (
                          <>
                            {" "}
                            <Badge tone="proforma" title="Gönderen müşterimiz; cariye alacak olarak işlenir">İade</Badge>
                          </>
                        )}
                      </td>
                      <td className="num amount">{formatCurrency(d.payableAmount, d.currencyCode)}</td>
                      <td className="hide-sm">
                        <div>{fmtDay(d.issueDate)}</div>
                        {d.receivedAt && <div className="ed-sub">geldi {fmtDateTime(d.receivedAt)}</div>}
                      </td>
                      <td className="hide-md">
                        {d.postedTo ? (
                          <Badge tone="ok" title={d.postedPartyName || undefined}>İşlendi</Badge>
                        ) : (
                          <Badge tone="muted">İşlenmedi</Badge>
                        )}
                      </td>
                      <td className="act" onClick={(e) => e.stopPropagation()}>
                        <div className="ed-rowacts">
                          <button className="ed-ib" title="Detay" onClick={() => openDetail(d.uuid)}>
                            {I.eye}
                          </button>
                          <button className="ed-ib" title="HTML görüntüle" disabled={busy} onClick={() => handleRender(d, "html")}>
                            {I.file}
                          </button>
                          <button className="ed-ib" title="PDF indir" disabled={busy} onClick={() => handleRender(d, "pdf")}>
                            {I.download}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Detay çekmecesi */}
      {detailId && (
        <div className="ed-overlay" onClick={closeDetail}>
          <aside className="ed-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="ed-loading">
                <span className="ed-spin" /> Detay yükleniyor
              </div>
            ) : (
              <>
                <div className="ed-drawer-head">
                  <div className="ed-drawer-title">
                    <span className="ed-kicker">
                      Gelen {detail.isReturn ? "iade faturası" : "fatura"} · {scenarioName(detail.scenario)}
                    </span>
                    <h2>{detail.invoiceNumber}</h2>
                    <div className="ed-badges">
                      <Badge tone={statusOf(detail).tone}>{statusOf(detail).text}</Badge>
                      {detail.isReturn && <Badge tone="proforma">İade</Badge>}
                      {detail.postedTo ? <Badge tone="ok">Cariye işlendi</Badge> : <Badge tone="muted">Cariye işlenmedi</Badge>}
                    </div>
                  </div>
                  <button className="ed-ib big" aria-label="Kapat" onClick={closeDetail}>
                    {I.close}
                  </button>
                </div>

                <div className="ed-drawer-body">
                  {actionMsg && (
                    <div className={`ed-notice ${actionMsg.ok ? "ok" : "err"}`}>
                      {actionMsg.ok ? I.check : I.alert}
                      <span>{actionMsg.text}</span>
                    </div>
                  )}

                  {/* Kimlik bilgileri */}
                  <dl className="ed-facts">
                    <div>
                      <dt>Gönderen</dt>
                      <dd>
                        {detail.senderName || "—"}
                        {detail.senderIdentifier && <small>VKN/TCKN {detail.senderIdentifier}</small>}
                      </dd>
                    </div>
                    <div>
                      <dt>Ödenecek tutar</dt>
                      <dd className="amount">{formatCurrency(detail.payableAmount, detail.currencyCode)}</dd>
                    </div>
                    <div>
                      <dt>Belge tarihi</dt>
                      <dd>
                        {fmtDay(detail.issueDate)}
                        {detail.receivedAt && <small>geldi {fmtDateTime(detail.receivedAt)}</small>}
                      </dd>
                    </div>
                    <div>
                      <dt>Senaryo / tip</dt>
                      <dd>
                        {scenarioName(detail.scenario)}
                        <small>{typeName(detail.invoiceType)}</small>
                      </dd>
                    </div>
                    <div>
                      <dt>{detail.isReturn ? "Müşteri kartı" : "Tedarikçi kartı"}</dt>
                      <dd>
                        {(detail.isReturn ? detail.firmName : detail.supplierName) || "Eşleşen kart yok"}
                        <small>VKN/TCKN ile eşleştirilir</small>
                      </dd>
                    </div>
                    <div>
                      <dt>ETTN</dt>
                      <dd className="ii-mono">{detail.uuid}</dd>
                    </div>
                  </dl>

                  {/* Çıktılar */}
                  <section className="ed-sec">
                    <h3>Çıktılar</h3>
                    <div className="ed-actions">
                      <button className="ed-btn" disabled={busy} onClick={() => handleRender(detail, "html")}>
                        {I.file} HTML aç
                      </button>
                      <button className="ed-btn" disabled={busy} onClick={() => handleRender(detail, "pdf")}>
                        {I.download} PDF indir
                      </button>
                      <button className="ed-btn" disabled={busy} onClick={() => handleRender(detail, "xml")}>
                        {I.download} XML indir
                      </button>
                      <button className="ed-btn" disabled={historyLoading} onClick={() => fetchHistory(detail.uuid)}>
                        {I.history} {historyLoading ? "Getiriliyor" : "Geçmiş"}
                      </button>
                    </div>
                  </section>

                  {/* Ticari fatura yanıtı */}
                  {detail.scenario === "TicariFatura" && (
                    <section className="ed-sec">
                      <h3>Kabul / red yanıtı</h3>
                      {detail.applicationResponse ? (
                        <p className="ed-form-hint">
                          Bu faturaya <b>{detail.applicationResponse === "RED" ? "red" : "kabul"}</b> yanıtı verilmiş.
                        </p>
                      ) : detail.canRespond ? (
                        <>
                          <p className="ed-form-hint">
                            Ticari faturaya yanıt süresi <b>{fmtDay(detail.responseDueDate)}</b> tarihinde doluyor
                            {detail.daysLeft === 0 ? " (bugün)" : ` (${detail.daysLeft} gün kaldı)`}. Süre dolunca fatura
                            kabul edilmiş sayılır. Yanıt hukuki bir beyandır ve geri alınamaz.
                          </p>
                          {canApprove ? (
                            <>
                              <div className="ed-actions">
                                <button
                                  className={`ed-btn ${respond.mode === "accept" ? "on" : ""}`}
                                  disabled={busy}
                                  onClick={() => setRespond({ mode: respond.mode === "accept" ? null : "accept", reason: "" })}
                                >
                                  {I.check} Kabul et
                                </button>
                                <button
                                  className={`ed-btn danger ${respond.mode === "reject" ? "on" : ""}`}
                                  disabled={busy}
                                  onClick={() => setRespond({ mode: respond.mode === "reject" ? null : "reject", reason: "" })}
                                >
                                  {I.x} Reddet
                                </button>
                              </div>
                              {respond.mode && (
                                <div className={`ed-form ${respond.mode === "reject" ? "danger" : ""}`}>
                                  <Field
                                    label={respond.mode === "reject" ? "Red gerekçesi" : "Açıklama"}
                                    hint={respond.mode === "reject" ? "zorunlu, göndericiye iletilir" : "isteğe bağlı"}
                                  >
                                    <input
                                      value={respond.reason}
                                      maxLength={500}
                                      placeholder={respond.mode === "reject" ? "Ör. fiyat farkı, eksik teslimat" : ""}
                                      onChange={(e) => setRespond({ ...respond, reason: e.target.value })}
                                    />
                                  </Field>
                                  <div className="ed-form-foot">
                                    <button className="ed-btn" onClick={() => setRespond({ mode: null, reason: "" })}>
                                      Vazgeç
                                    </button>
                                    <button
                                      className={`ed-btn ${respond.mode === "reject" ? "danger solid" : "primary"}`}
                                      disabled={busy || (respond.mode === "reject" && respond.reason.trim().length < 5)}
                                      onClick={handleRespond}
                                    >
                                      {busy ? "Gönderiliyor" : respond.mode === "reject" ? "Red yanıtını gönder" : "Kabul yanıtını gönder"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="ed-form-hint">Yanıt vermek için "Gelen Faturalar › Onayla / Reddet" yetkisi gerekir.</p>
                          )}
                        </>
                      ) : (
                        <p className="ed-form-hint">Yanıt süresi doldu; fatura kabul edilmiş sayılır.</p>
                      )}
                    </section>
                  )}

                  {/* Cari */}
                  <section className="ed-sec">
                    <h3>Cari</h3>
                    {detail.postedTo ? (
                      <div className="ii-posted">
                        {I.ledger}
                        <span>
                          {detail.postedTo === "firm" ? "Müşteri carisine alacak olarak işlendi: " : "Tedarikçi carisine işlendi: "}
                          <b>{detail.postedPartyName}</b>
                          {detail.postedTo === "supplier" && detail.supplierId && financeOn && (
                            <> · <Link to={`/payables?supplier=${detail.supplierId}`}>ekstreyi aç</Link></>
                          )}
                          {detail.postedTo === "firm" && detail.firmId && receivablesOn && (
                            <> · <Link to={`/receivables/firm/${detail.firmId}`}>ekstreyi aç</Link></>
                          )}
                        </span>
                      </div>
                    ) : !canPost ? (
                      <p className="ed-form-hint">Cariye işlemek için "Gelen Faturalar › Cariye İşle" yetkisi gerekir.</p>
                    ) : postBlocked ? (
                      <p className="ed-form-hint">
                        {detail.isReturn
                          ? "İade faturası müşteri carisine işlenir; Cari Hesap modülü lisansınızda yok."
                          : "Alış faturası tedarikçi carisine işlenir; Finans modülü lisansınızda yok."}
                      </p>
                    ) : detail.isReturn ? (
                      <div className="ed-form">
                        <p className="ed-form-hint">
                          İade faturasını kesen, size daha önce mal satılan taraftır: tutar müşterinin carisine
                          <b> alacak</b> olarak düşer.
                        </p>
                        <Field label="Müşteri firma">
                          <select value={post.firmId} onChange={(e) => setPost({ ...post, firmId: e.target.value })}>
                            <option value="">Firma seçin</option>
                            {(parties?.firms ?? []).map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}{f.taxNumber ? ` · ${f.taxNumber}` : ""}{f.isActive ? "" : " (pasif)"}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <div className="ed-form-foot">
                          <button className="ed-btn primary" disabled={busy || !post.firmId} onClick={handlePost}>
                            {busy ? "İşleniyor" : "Müşteri carisine işle"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ed-form">
                        <Field label="Tedarikçi" hint={detail.supplierId ? "VKN ile eşleşti" : undefined}>
                          <select
                            value={post.supplierId}
                            onChange={(e) => setPost({ ...post, supplierId: e.target.value, matchEntryId: "" })}
                          >
                            <option value="">Tedarikçi seçin</option>
                            {!detail.supplierId && (
                              <option value={NEW_SUPPLIER}>
                                + Yeni kart: {detail.senderName || detail.senderIdentifier}
                              </option>
                            )}
                            {(parties?.suppliers ?? []).map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}{s.taxNumber ? ` · ${s.taxNumber}` : ""}{s.isActive ? "" : " (pasif)"}
                              </option>
                            ))}
                          </select>
                        </Field>
                        {post.supplierId === NEW_SUPPLIER && (
                          <p className="ed-form-hint">
                            Faturadaki unvan ve VKN ile yeni tedarikçi kartı açılır (aynı VKN'li ya da aynı adlı kart
                            varsa o kullanılır). Vade, adres ve iletişim bilgilerini sonra Tedarikçiler ekranından tamamlayın.
                          </p>
                        )}

                        {candidates.length > 0 && (
                          <div className="ii-choice">
                            <span className="ii-choice-label">
                              Bu tedarikçide faturası beklenen mal kabul kayıtları var — fatura bunlardan birine aitse
                              eşleştirin, borç iki kez yazılmasın.
                            </span>
                            <label className={post.matchEntryId === "" ? "on" : ""}>
                              <input type="radio" name="ii-match" checked={post.matchEntryId === ""} onChange={() => setPost({ ...post, matchEntryId: "" })} />
                              <span>Yeni borç kaydı olarak işle</span>
                            </label>
                            {candidates.map((c) => (
                              <label key={c.entryId} className={post.matchEntryId === c.entryId ? "on" : ""}>
                                <input
                                  type="radio"
                                  name="ii-match"
                                  checked={post.matchEntryId === c.entryId}
                                  onChange={() => setPost({ ...post, matchEntryId: c.entryId })}
                                />
                                <span>
                                  Mal kabul {new Date(c.entryDate).toLocaleDateString("tr-TR")}
                                  {c.purchaseOrderNumber ? ` · ${c.purchaseOrderNumber}` : ""} ·{" "}
                                  <b>{formatCurrency(c.amount, c.currency)}</b>
                                  {c.referenceNo ? <small> · {c.referenceNo}</small> : null}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        <Field label="Vade" hint={selectedSupplier ? "boşsa fatura tarihi + tedarikçinin vadesi" : "boşsa fatura tarihi + 30 gün"}>
                          <input type="date" value={post.dueDate} onChange={(e) => setPost({ ...post, dueDate: e.target.value })} />
                        </Field>

                        <div className="ed-form-foot">
                          <button className="ed-btn primary" disabled={busy || !post.supplierId} onClick={handlePost}>
                            {busy
                              ? "İşleniyor"
                              : post.matchEntryId
                                ? "Mal kabul kaydıyla eşleştir"
                                : post.supplierId === NEW_SUPPLIER
                                  ? "Kartı aç ve carisine işle"
                                  : "Tedarikçi carisine işle"}
                          </button>
                        </div>
                      </div>
                    )}
                  </section>

                  {/* Geçmiş */}
                  {history && (
                    <section className="ed-sec">
                      <h3>Geçmiş</h3>
                      {history.length === 0 ? (
                        <p className="ed-form-hint">Serviste kayıtlı olay yok.</p>
                      ) : (
                        <ol className="ed-timeline">
                          {history.map((h, i) => (
                            <li key={i} className={h.ok === false ? "err" : h.ok ? "ok" : ""}>
                              <i />
                              <div>
                                <div className="ed-tl-top">
                                  <Badge tone={h.ok === false ? "err" : h.kind === "state" ? "info" : "ok"}>{h.title}</Badge>
                                  {h.at && <time>{fmtDateTime(h.at)}</time>}
                                </div>
                                {h.detail && <div className="ed-sub">{h.detail}</div>}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </section>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default IncomingInvoices;
